// React
import React, { useState, useEffect, useRef } from 'react';

// Mantine
import {
  Autocomplete,
  AutocompleteProps,
  Avatar,
  Group,
  Text,
  Loader,
  OptionsFilter,
  useComputedColorScheme,
  TextInput,
} from '@mantine/core';
import { useDebouncedValue } from '@mantine/hooks';

import { useActiveForm } from 'pages/claims/components';

// Resource
import { useSearchProviders } from 'api/resource';

// Interface
import { Payor, Provider } from 'interfaces';
import { IconX } from '@tabler/icons-react';
import { getValueByPath } from 'utils/getValueByPath';

type ProviderSearchOption = {
  value: string;
  label: string;
  isLookupFallback?: boolean;
};

/**
 * Props for the `ProviderSearch` component.
 * @interface ProviderSearchProps
 * @property {string} [defaultValue] - The default text for the search input field
 * @property {Payor[]} payors - An array of payor objects to be used in the component
 */
interface ProviderSearchProps {
  defaultValue?: string;
  payors: Payor[];
  required: boolean;
  disabled?: boolean;
}

/**
 * The `ProviderSearch` component provides an autocomplete field for searching and selecting providers.
 * It uses debounced search input and displays provider data in a dropdown with custom rendering for options.
 * @category Component
 * @param {ProviderSearchProps} props - Component props (see interface above for more information)
 *
 * @example
 * <ProviderSearch setter={setProviderID} />
 */

export const ProviderSearch: React.FC<ProviderSearchProps> = ({
  defaultValue = '',
  payors,
  required = false,
  disabled = false,
}) => {
  const [searchValue, setSearchValue] = useState<string>('');
  const [data, setData] = useState<any>([]);
  const [debounced] = useDebouncedValue<string>(searchValue, 500);
  const computedColorScheme = useComputedColorScheme('dark');
  const color = computedColorScheme === 'dark' ? 'white' : 'black';
  const { form, prefix, isBulk } = useActiveForm();
  // const ref = useRef<HTMLInputElement>(null);
  const autoInputRef = useRef<HTMLInputElement>(null);
  const readonlyRef = useRef<HTMLInputElement>(null);
  const focusRequestedRef = useRef(false);

  const payorIDPath = prefix ? `${prefix}.payorID` : 'payorID';
  const referringProviderPath = prefix
    ? `${prefix}.referringProvider`
    : 'referringProvider';

  const payorID: string | undefined = getValueByPath(
    form.getValues(),
    payorIDPath
  );

  const referringProvider: string | undefined = getValueByPath(
    form.getValues(),
    referringProviderPath
  );
  const referringProviderObjectPath = prefix
    ? `${prefix}.referringProviderObject`
    : 'referringProviderObject';

  const referringProviderObject: Provider | undefined = getValueByPath(
    form.getValues(),
    referringProviderObjectPath
  );

  const payorName = payors.find((payor) => payor.id === payorID)?.name;

  const label = referringProviderObject
    ? referringProviderObject?.firstName && referringProviderObject?.lastName
      ? `${referringProviderObject.firstName} ${referringProviderObject.lastName}`
      : referringProvider
    : '';

  const {
    data: providerResult,
    isLoading,
    isFetching,
  } = useSearchProviders({
    searchValue: debounced,
  });
  const providers = providerResult as Provider[];

  useEffect(() => {
    if (providers?.length === 0 || !payorName) {
      setData([]);
      return;
    }

    const optionAccumulator: ProviderSearchOption[] = [];
    const seenValues = new Set<string>(); // ensure unique option values (Mantine requires this)

    providers?.forEach((provider) => {
      // Collect all licenses for this payor, then dedupe identical license strings for this provider
      const payorLicenses = provider.licenses
        ?.filter((l) => l.payorName === payorName)
        .map((l) => l.license)
        .filter(Boolean) as string[];

      const uniqueLicenses = Array.from(new Set(payorLicenses));

      if (uniqueLicenses.length) {
        uniqueLicenses.forEach((license) => {
          const compositeValue = `${provider.id}::${license}`; // composite ensures uniqueness even if license duplicated across providers
          if (seenValues.has(compositeValue)) return; // skip duplicates
          seenValues.add(compositeValue);
          optionAccumulator.push({
            value: compositeValue,
            label: `${provider.firstName} ${provider.lastName}`,
          });
        });
        return;
      }

      const lookupLicense = provider.license?.trim();
      if (lookupLicense) {
        const compositeValue = `${provider.id}::${lookupLicense}`;
        if (seenValues.has(compositeValue)) return;
        seenValues.add(compositeValue);
        optionAccumulator.push({
          value: compositeValue,
          label: `${provider.firstName} ${provider.lastName} (provider lookup)`,
          isLookupFallback: true,
        });
      }
    });

    if (!optionAccumulator.length) {
      setData([
        {
          value: 'N/A',
          label: 'No matching licenses for the selected payor',
        },
      ]);
    } else {
      setData(optionAccumulator);
    }
  }, [providers, payorName]);
  const renderAutocompleteOption: AutocompleteProps['renderOption'] = ({
    option,
  }: {
    option: ProviderSearchOption;
  }) => {
    if (!option) return <Loader />;

    if (option.value === 'N/A') {
      return (
        <Text size="sm" c="dimmed">
          No matching licenses for the selected payor
        </Text>
      );
    }

    if (!payorName) {
      return (
        <Text size="sm" c="red">
          Payor not found. Please select a payor first.
        </Text>
      );
    }

    // Parse composite value providerId::license
    const [providerID, licenseValue] = option.value.split('::');

    const matchedProvider = providers?.find((p) => p.id === providerID);

    if (!matchedProvider) {
      return (
        <Text size="sm" c="red">
          Provider license not found for selected payor {payorName}
        </Text>
      );
    }

    const matchedLicense = matchedProvider.licenses?.find(
      (l) => l.payorName === payorName && l.license === licenseValue
    )?.license;
    const isLookupFallback = option.isLookupFallback || !matchedLicense;

    return (
      <Group gap="sm">
        <Avatar
          key={option.value}
          name={`${matchedProvider.firstName} ${matchedProvider.lastName}`}
          color="initials"
        />
        <div>
          <Text size="sm">{`${matchedProvider.firstName} ${matchedProvider.lastName}`}</Text>
          <Text size="xs" opacity={0.5}>
            {isLookupFallback ? 'Lookup license' : 'License'}:{' '}
            {isLookupFallback ? licenseValue : matchedLicense}
          </Text>
          {isLookupFallback && (
            <Text size="xs" opacity={0.5}>
              No selected-payor license
            </Text>
          )}
          <Text size="xs" opacity={0.5}>
            Specialty: {matchedProvider?.specialtyCode}
          </Text>
        </div>
      </Group>
    );
  };

  const selectValue = (value: string) => {
    if (value === 'N/A') return;
    const [providerID, licenseValue] = value.split('::');
    const selectedProvider =
      providers?.find((provider) => provider.id === providerID) ?? null;

    // Store only the raw license string in referringProvider (maintains previous contract)
    form.setFieldValue(referringProviderPath, licenseValue || '');
    form.setFieldValue(referringProviderObjectPath, selectedProvider);
    focusRequestedRef.current = true;
  };

  useEffect(() => {
    const resolved = !!referringProvider && !!referringProviderObject;
    if (!resolved || !focusRequestedRef.current) return;

    requestAnimationFrame(() => {
      readonlyRef.current?.focus();
      focusRequestedRef.current = false; // only once per selection
    });
  }, [referringProvider, referringProviderObject]);

  const optionsFilter: OptionsFilter = ({ options }) => options;

  if (referringProvider && referringProviderObject) {
    return (
      <TextInput
        error={form.errors?.[referringProviderPath]}
        label="Referring Provider"
        ref={readonlyRef}
        value={label}
        readOnly
        style={{ width: '100%' }}
        size="md"
        radius="md"
        c={color}
        disabled={disabled}
        rightSection={
          <IconX
            size={16}
            onClick={() => {
              form.setFieldValue(referringProviderPath, '');
              form.setFieldValue(referringProviderObjectPath, null);
              setSearchValue('');
              requestAnimationFrame(() => autoInputRef.current?.focus());
            }}
          />
        }
      />
    );
  }

  if (disabled) {
    return (
      <TextInput
        error={form.errors?.[referringProviderPath]}
        label="Referring Provider"
        value={label}
        style={{ width: '100%' }}
        size="md"
        radius="md"
        c={color}
        disabled
      />
    );
  }

  return (
    <Autocomplete
      error={form.errors?.[referringProviderPath]}
      label="Referring Provider"
      comboboxProps={{
        withinPortal: true,
      }}
      ref={autoInputRef}
      defaultValue={defaultValue}
      required={required}
      style={{ width: '100%' }}
      radius="md"
      size="md"
      c={color}
      description={
        isBulk && !payorName
          ? 'Select a payor to search referring providers for claims with different payors.'
          : undefined
      }
      onChange={(value) => setSearchValue(value)}
      renderOption={renderAutocompleteOption}
      onOptionSubmit={selectValue}
      filter={optionsFilter}
      data={data}
      rightSection={
        (isLoading || isFetching) && (
          <Loader
            style={{ position: 'absolute', right: 15, top: 10 }}
            size={20}
            color={color}
          />
        )
      }
      disabled={disabled}
    />
  );
};
